// Setups that survived MoneyHQ's backtests (Oct 2026: 8 years of NSE daily data, after costs, against
// placebos), in the exact form tested, so what the chart draws is what was measured.

/** One daily candle (oldest first). */
export interface Day { date: string; o: number; h: number; l: number; c: number; v: number }

/**
 * New-high breakout with a wide trailing stop (Wilcox-Crittenden style; research spec A2):
 *  signal: the close is the highest close of the previous 252 sessions and of all earlier history
 *          (at least 252 sessions needed first);
 *  entry:  the next session's open;
 *  stop:   the highest close since entry - 10 x ATR(20), only ever rising; the trade ends at the next
 *          open after a close below it. No target. A new signal while in a trade is ignored.
 * `stop[k]` is the stop in force during session `entryIdx + k` (what that session's close is judged
 * against); `stopNow` is the stop after the latest close (for an open trade, today's level).
 */
export interface Breakout {
  signalIdx: number;
  entryIdx: number;
  entry: number;
  /** The session whose open ended the trade, or null while it is still open. */
  exitIdx: number | null;
  exit: number | null;
  stop: number[];
  stopNow: number;
}

export const BREAKOUT_LOOKBACK = 252;
export const BREAKOUT_ATR = 20;
export const BREAKOUT_ATR_MULT = 10;

/** Average true range: the simple mean of the last n true ranges, at each session (NaN before). */
export function atrSeries(d: Day[], n: number): number[] {
  const out = new Array(d.length).fill(NaN);
  let sum = 0;
  const tr = d.map((x, i) => (i === 0 ? x.h - x.l : Math.max(x.h, d[i - 1].c) - Math.min(x.l, d[i - 1].c)));
  for (let i = 0; i < d.length; i++) {
    sum += tr[i];
    if (i >= n) sum -= tr[i - n];
    if (i >= n) out[i] = sum / n;
  }
  return out;
}

export function highBreakouts(d: Day[]): Breakout[] {
  const out: Breakout[] = [];
  const atr = atrSeries(d, BREAKOUT_ATR);
  let allMax = -Infinity, open: Breakout | null = null, best = -Infinity, stop = 0, blockUntil = -1;
  for (let i = 0; i < d.length; i++) {
    const prevAll = allMax;
    allMax = Math.max(allMax, d[i].c);
    if (open) {
      // The close is judged against the stop in force today; then the stop trails up.
      open.stop.push(stop);
      if (d[i].c < stop) {
        if (i + 1 < d.length) { open.exitIdx = i + 1; open.exit = d[i + 1].o; open = null; blockUntil = i + 1; continue; }
      }
      best = Math.max(best, d[i].c);
      if (Number.isFinite(atr[i])) stop = Math.max(stop, best - BREAKOUT_ATR_MULT * atr[i]);
      open.stopNow = stop;
      continue;
    }
    if (i <= blockUntil || i < BREAKOUT_LOOKBACK || i + 1 >= d.length || !(atr[i] > 0)) continue;
    let max252 = -Infinity;
    for (let k = i - BREAKOUT_LOOKBACK; k < i; k++) max252 = Math.max(max252, d[k].c);
    if (!(d[i].c >= max252 && d[i].c >= prevAll)) continue;
    const e = i + 1;
    stop = d[e].o - BREAKOUT_ATR_MULT * atr[i];
    if (!(d[e].o - stop > 0)) continue; // no measurable risk (a flat stock)
    best = -Infinity;
    open = { signalIdx: i, entryIdx: e, entry: d[e].o, exitIdx: null, exit: null, stop: [], stopNow: stop };
    out.push(open);
  }
  return out;
}
